<script lang="ts">
    import {Icon, Page, PageContent} from 'framework7-svelte';
    import {tick} from 'svelte';
    import { _ } from 'svelte-i18n';
    
    import {indicators} from '../utils/indicators';
    import Statistics from './Dashboard/Statistics.svelte';
    import RouteFilter from './Dashboard/RouteFilter.svelte';
    import DashboardGraph from './Dashboard/DashboardGraph.svelte';
    import InsightMap from "./InsightMap.svelte";
    import {projectStore} from "../stores/project.store";
    import {routeMeasurementsMappedStore} from "../stores/route-measurements-mapped.store";
    import type {RouteMeasurements} from "../models/route-measurements.model";
    import {MEASUREMENT_FIELD_TIMESTAMP} from "../constants/measurement-table-constants";
    import {datesAreEqual, formatDateString, toDateOnly, toTimeOnly} from "../helpers/date.helper";
    import type {DashboardMeasurementFilter} from "../types/filters/dashboard-measurement-filter.type";

    $: id = `project-${$projectStore.data?.id}`;
    
    let fullscreen = false;
    let activeMeasurementProperty: string;
    let collapsed = false;
    
    let activeFilter: DashboardMeasurementFilter = {
        dateRange: {
            startDate: undefined, 
            endDate: undefined
        },
        timeRange: {
            startTime: null,
            endTime: null
        },
        weekdays: [
            $_('shared.MONDAY_SHORT'),
            $_('shared.TUESDAY_SHORT'),
            $_('shared.WEDNESDAY_SHORT'),
            $_('shared.THURSDAY_SHORT'),
            $_('shared.FRIDAY_SHORT'),
            $_('shared.SATURDAY_SHORT'),
            $_('shared.SUNDAY_SHORT')
        ]
    }
    
    let measurementTimeStampsFiltered: Date[] = []

    const datePickerWeekdayIndexes: {[key: string]: number} = {
        [$_('shared.MONDAY_SHORT')]: 1,
        [$_('shared.TUESDAY_SHORT')]: 2,
        [$_('shared.WEDNESDAY_SHORT')]: 3,
        [$_('shared.THURSDAY_SHORT')]: 4,
        [$_('shared.FRIDAY_SHORT')]: 5,
        [$_('shared.SATURDAY_SHORT')]: 6,
        [$_('shared.SUNDAY_SHORT')]: 0
    }

    $: indicatorIdx = indicators.findIndex((x) => x.id === activeMeasurementProperty);
    $: routeMeasurementsFiltered = applyMeasurementsFilter($routeMeasurementsMappedStore, activeFilter);
    
    function applyMeasurementsFilter(allRouteMeasurements: RouteMeasurements[], filter: DashboardMeasurementFilter): RouteMeasurements[] {
        let distinctMeasurementTimestamps = new Map<string, Date>();
        
        const dateRangeFilterActive = filter.dateRange.startDate && filter.dateRange.endDate;
        const singleDateFilterActive = !dateRangeFilterActive && filter.dateRange.startDate;
        
        const timeRangeFilterActive = filter.timeRange.startTime && filter.timeRange.endTime;
        const startTime = timeRangeFilterActive ? toTimeOnly(filter.timeRange.startTime) : null;
        const endTime = timeRangeFilterActive ? toTimeOnly(filter.timeRange.endTime) : null;

        const weekdaysFilterActive = filter.weekdays.length > 0 && filter.weekdays.length < 7;
        const selectedWeekdays = new Set(
            filter.weekdays.map((dayAsString) => datePickerWeekdayIndexes[dayAsString])
        );
        
        const filteredRouteMeasurements = allRouteMeasurements.filter((routeMeasurement) => {
            const measurementDate = singleDateFilterActive || dateRangeFilterActive 
                ? toDateOnly(routeMeasurement[MEASUREMENT_FIELD_TIMESTAMP])
                : null;
            
            const measurementTime = timeRangeFilterActive 
                ? toTimeOnly(routeMeasurement[MEASUREMENT_FIELD_TIMESTAMP])
                : null;
            
            const passesSingleDateFilter = singleDateFilterActive 
                ? datesAreEqual(measurementDate, filter.dateRange.startDate)
                : true;
            if(!passesSingleDateFilter) return false;
            
            const passesDateRangeFilter = dateRangeFilterActive 
                ? measurementDate >= filter.dateRange.startDate 
                    && measurementDate <= filter.dateRange.endDate
                : true;
            if(!passesDateRangeFilter) return false;
            
            const passesTimeRangeFilter = timeRangeFilterActive 
                ? measurementTime >= startTime
                    && measurementTime <= endTime
                : true;
            if(!passesTimeRangeFilter) return false;
            
            const passesWeekdaysFilter = weekdaysFilterActive 
                ? selectedWeekdays.has(routeMeasurement[MEASUREMENT_FIELD_TIMESTAMP].getDay())
                : true;
            if(!passesWeekdaysFilter) return false;
            
            
            const dateString = formatDateString(routeMeasurement[MEASUREMENT_FIELD_TIMESTAMP]);
            if(!distinctMeasurementTimestamps.has(dateString)) {
                distinctMeasurementTimestamps.set(
                    dateString, routeMeasurement[MEASUREMENT_FIELD_TIMESTAMP]
                );
            }
            
            return true;
        })
        
        measurementTimeStampsFiltered = Array.from(distinctMeasurementTimestamps.values());
        return filteredRouteMeasurements;
    }
    
    
    function selectMeasurementProperty(propertyKey: string) {
        activeMeasurementProperty = propertyKey;
        repaint();
    }

    async function handlerFilterChange(event: CustomEvent<DashboardMeasurementFilter>) {
        if(activeFilter === event.detail) return;
        
        activeFilter = { ...event.detail };
    }

    async function repaint() {
        // @TODO: deze functie doet niks! hij wacht alleen tot de render klaar is
        tick();
    }
    
    function toggleCollapsed() {
        collapsed = !collapsed;
    }

    function toggleFullscreen() {
        fullscreen = !fullscreen;
    }
</script>

<Page pageContent={false} {id} class="insights-dashboard">
    <PageContent>
        <div class="dashboard-layout">
            <Statistics {selectMeasurementProperty} {routeMeasurementsFiltered} />
            <RouteFilter on:changeFilter={handlerFilterChange}/>
            
            <div class="cards-container" class:collapsed>
                {#if $routeMeasurementsMappedStore.length > 0}
                <div class="card left-card">
                    <button on:click={toggleCollapsed} class="map-collapse-btn">
                        {#if collapsed}
                            <Icon material="chevron_right" />
                        {:else}
                            <Icon material="chevron_left" />
                        {/if}
                    </button>
                    <InsightMap
                        trafficFlowMode={'none'}
                        {indicatorIdx}
                        congestionProfile={[]}
                        showSpeedValues={false}
                        showNdwFlow={false}
                        showNdwSpeed={false}
                        modeNdwActive={false}
                        modeNdwInactive={false}
                    />
                </div>
    
                <div class="card right-card {fullscreen ? 'fullscreen' : ''}">
                    <div class="card-content">
                        <button on:click={toggleFullscreen} class="fullscreen-toggle-btn">
                            <Icon material="aspect_ratio" />
                        </button>
                            <DashboardGraph
                                measurementTimeStamps={measurementTimeStampsFiltered}
                                measurementsForGraph={routeMeasurementsFiltered}
                                {activeMeasurementProperty}
                                showMeasurementPropertySelector={false}
                            />
                    </div>
                </div>
                {/if}
            </div>
        </div>
    </PageContent>
</Page>

<style lang="scss">
    @import '../assets/scss/colors';

    :global(.insights-dashboard) {
        background-color: #{$background};
    }

    :global(.btn) {
        border-radius: 100px;
        padding: 8px;
        i {
            width: 24px;
            height: 24px;
        }
    }
    :global(.btn-graph) {
        border-radius: 100px;
        background: var(--Buko-sys-light-secondary-container, #ffdad6);
        color: #{$secondary-fixed-on};
    }

    :global(.btn-menu) {
        color: #{$surface-on-variant};
    }

    .dashboard-layout {
      min-height: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .cards-container {
      flex: 1;
      min-height: 0;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--f7-card-margin-horizontal);
      transition: grid-template-columns 0.3s ease;
    }
    .cards-container.collapsed {
        grid-template-columns: 77px 1fr;
    }

    .left-card {
        overflow: hidden;
        margin-right: 0;
    }

    .right-card {
        margin-left: 0;
    }

    @media (max-width: 768px) {
        .cards-container {
            grid-template-columns: 1fr;
            gap: 0;

            .left-card,
            .right-card {
                margin: 16px;
            }

            .left-card {
                margin-bottom: 0;
            }
        }

        .cards-container.collapsed {
            grid-template-columns: 1fr;
        }
    }

    .right-card {
      position: relative;
      background: var(--f7-card-bg-color, #fff);
      border-radius: 12px;
      bottom: 0;
      overflow: hidden;
      transform: scale(1);
      opacity: 1;
      transition:
              transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1),
              border-radius 0.35s ease,
              box-shadow 0.35s ease;
    }

    :global(.right-card .card-content) {
      height: 100%;
      min-height: 0;
      box-sizing: border-box;
    }

    .right-card.fullscreen {
      position: fixed;
      inset: 0;
      z-index: 9999;
      border-radius: 0;
      transform: scale(1);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      height: calc(100% - 48px);
      margin-top: 48px;
      margin-left: calc(var(--f7-card-margin-horizontal) + var(--f7-safe-area-left));
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    .map-collapse-btn,
    .fullscreen-toggle-btn {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 40px;
        height: 40px;
        z-index: 999;
        border-radius: 8px;
        border: none;
        background-color: $secondary-container;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        cursor: pointer;
    }
</style>
